import React, { useState, useEffect, useMemo } from 'react';
import { Printer, ArrowUpDown, Pencil, Trash2 } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import { getPlayers, getTests, getTestNotes, deleteTestSession, getISRTPercentiles, getSprintPercentiles, getSquadProfiling } from '../api';
import {
  MOBILITY_TESTS, STRENGTH_TESTS, avg2,
  lsiPct, lsiColor, hqRatio, hqColor, abAdRatio, abAdColor, getISRTColor, getPercentileColor, cmjPeakPowerWatts,
  SPRINT_ISRT_METRIC,
} from './physicalTestingShared';
import QuadrantMatrix from '../components/QuadrantMatrix';
import PassingDrillDetailModal from './PassingDrillDetailModal';

const TEST_TYPES = ['mobility', 'strength', 'sprint', 'cmj', 'hop', 'isrt', 'weight', 'skinfold', 'passing-drill', 'dropjump', 'dropjump_sl'];

// 'ratio' metrics carry their own color function (LSI/ratio thresholds); plain metrics are uncolored numbers.
// 'trend' says which direction is an improvement, for the small vs-previous-test badge:
// 'lower' = a decrease is green (e.g. sprint times), 'higher' = an increase is green (e.g. CMJ height),
// 'neutral' = no fixed direction (e.g. Weight, H:Q — shown in grey either way), omitted = no trend badge at all.
const TEAM_METRICS = [
  { key: 'position',        label: 'Position',          type: 'text' },
  { key: 'sprint5m',        label: 'Sprint 5m',         digits: 2, trend: 'lower' },
  { key: 'sprint10m',       label: 'Sprint 10m',        digits: 2, trend: 'lower' },
  { key: 'sprint10mPercentile', label: 'Sprint 10m %', type: 'ratio', digits: 0, suffix: '%', color: getPercentileColor },
  { key: 'sprint30m',       label: 'Sprint 30m',        digits: 2, trend: 'lower' },
  { key: 'sprint30mPercentile', label: 'Sprint 30m %', type: 'ratio', digits: 0, suffix: '%', color: getPercentileColor },
  { key: 'cmjHeight',       label: 'CMJ Height',        digits: 1, trend: 'higher' },
  { key: 'cmjPeakPower',    label: 'CMJ Peak Power (W/kg)', digits: 1, trend: 'higher' },
  { key: 'cmjPeakPowerW',   label: 'CMJ Peak Power (W)', digits: 0, trend: 'higher' },
  { key: 'hopRsi',          label: 'Hop Mean RSI',      digits: 2, trend: 'higher' },
  { key: 'isrtStage',       label: 'ISRT Stage',        digits: 0, trend: 'higher' },
  { key: 'isrtPercentile',  label: 'ISRT %', type: 'ratio', digits: 0, suffix: '%', color: getISRTColor },
  { key: 'maxHR',           label: 'Max HR',            digits: 0, trend: 'neutral' },
  { key: 'adjPctHRmax',     label: 'Passing Drill %HRmax', digits: 1, trend: 'neutral' },
  { key: 'weight',          label: 'Weight',            digits: 1, trend: 'neutral' },
  { key: 'sum8',            label: 'Sum-8 Skinfold',    digits: 1, trend: 'lower' },
  { key: 'hamstring',       label: 'Strength Hamstring', digits: 1, trend: 'higher' },
  { key: 'quadriceps',      label: 'Strength Quadriceps', digits: 1, trend: 'higher' },
  { key: 'hq',   label: 'Ratio H:Q',   type: 'ratio', digits: 2, color: hqColor, trend: 'neutral' },
  { key: 'abad', label: 'Ratio AB:AD', type: 'ratio', digits: 0, suffix: '%', color: abAdColor, trend: 'neutral' },
];

function latestByPlayer(rows) {
  const map = {};
  for (const r of rows || []) {
    if (!map[r.player_id]) map[r.player_id] = r;
  }
  return map;
}

// Second-most-recent test per player — used as the "previous" test for trend badges.
// Relies on rows already being sorted by test_date DESC (as returned by the API): the
// first time a player_id is seen is their latest test, the second time is their previous one.
function secondByPlayer(rows) {
  const seen = new Set();
  const map = {};
  for (const r of rows || []) {
    if (!seen.has(r.player_id)) { seen.add(r.player_id); continue; }
    if (!(r.player_id in map)) map[r.player_id] = r;
  }
  return map;
}

function fmt(v, digits = 1) {
  return v === null || v === undefined || v === '' || isNaN(v) ? '—' : Number(v).toFixed(digits);
}

// % change from previous test to latest test, rounded to a whole number. Null if either
// value is missing or the previous value is 0 (can't compute a meaningful % change from zero).
function pctChange(curr, prev) {
  if (curr === null || curr === undefined || curr === '' || isNaN(curr)) return null;
  if (prev === null || prev === undefined || prev === '' || isNaN(prev) || Number(prev) === 0) return null;
  return Math.round(((Number(curr) - Number(prev)) / Math.abs(Number(prev))) * 100);
}

// Green if the change is an improvement given the metric's trend direction, red if not,
// grey for neutral-direction metrics or an unchanged value.
function trendColor(pct, direction) {
  if (pct === 0 || direction === 'neutral') return 'var(--text-dimmed)';
  const improved = direction === 'higher' ? pct > 0 : pct < 0;
  return improved ? '#10b981' : '#ef4444';
}

function LRRowDisplay({ label, left, right }) {
  const pct = lsiPct(left, right);
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1.4fr 1fr 1fr 80px', gap: 10, alignItems: 'center', padding: '6px 0', borderBottom: '1px solid var(--border-strong)', fontSize: 13 }}>
      <div style={{ fontWeight: 600 }}>{label}</div>
      <div>{left ?? '—'}</div>
      <div>{right ?? '—'}</div>
      <div style={{ fontWeight: 700, color: lsiColor(pct), textAlign: 'center' }}>{pct !== null ? `${pct.toFixed(0)}%` : '—'}</div>
    </div>
  );
}

export default function PhysicalTesting({ onNavigate }) {
  const [view, setView] = useState('team'); // team | individual
  const [players, setPlayers] = useState([]);
  const [filter, setFilter] = useState('');
  const [visibleMetrics, setVisibleMetrics] = useState(TEAM_METRICS.map(m => m.key));
  const [selectedPlayerId, setSelectedPlayerId] = useState('');
  const [allData, setAllData] = useState({}); // { type: [rows] } — team-wide latest
  const [playerData, setPlayerData] = useState({}); // { type: [rows] } — full history for selected player
  const [loadingTeam, setLoadingTeam] = useState(true);
  const [loadingPlayer, setLoadingPlayer] = useState(false);
  const [playerNotes, setPlayerNotes] = useState([]);
  const [sortKey, setSortKey] = useState('date');
  const [sortDir, setSortDir] = useState('desc');
  const [teamSortKey, setTeamSortKey] = useState('name');
  const [teamSortDir, setTeamSortDir] = useState('asc');
  const [isrtPercentiles, setIsrtPercentiles] = useState([]);
  const [sprintPercentiles, setSprintPercentiles] = useState([]);
  const [squadProfiling, setSquadProfiling] = useState([]);
  const [showTrend, setShowTrend] = useState(true);
  const [showPassingDrillDetail, setShowPassingDrillDetail] = useState(false);

  useEffect(() => {
    getPlayers().then(p => setPlayers(p || [])).catch(() => {});
    Promise.all(['sprint', 'cmj', 'isrt', 'weight', 'skinfold', 'hop', 'strength', 'passing-drill'].map(t => getTests(t)))
      .then(([sprint, cmj, isrt, weight, skinfold, hop, strength, passingDrill]) => {
        setAllData({ sprint, cmj, isrt, weight, skinfold, hop, strength, passingDrill });
      })
      .catch(() => {})
      .finally(() => setLoadingTeam(false));
    getISRTPercentiles().then(rows => setIsrtPercentiles(rows || [])).catch(() => {});
    getSprintPercentiles().then(rows => setSprintPercentiles(rows || [])).catch(() => {});
    getSquadProfiling().then(rows => setSquadProfiling(rows || [])).catch(() => {});
  }, []);

  const fetchPlayerData = id => {
    setLoadingPlayer(true);
    return Promise.all(TEST_TYPES.map(t => getTests(t, id)))
      .then(results => {
        const data = {};
        TEST_TYPES.forEach((t, i) => { data[t] = results[i] || []; });
        setPlayerData(data);
      })
      .catch(() => {})
      .finally(() => setLoadingPlayer(false));
  };

  useEffect(() => {
    if (!selectedPlayerId) return;
    fetchPlayerData(selectedPlayerId);
    getTestNotes(selectedPlayerId).then(rows => setPlayerNotes(rows || [])).catch(() => setPlayerNotes([]));
  }, [selectedPlayerId]);

  const handleEditSession = date => {
    localStorage.setItem('edit_test_session', JSON.stringify({ playerId: selectedPlayerId, date }));
    if (onNavigate) onNavigate('testinput');
  };

  const handleDeleteSession = async date => {
    if (!window.confirm(`Delete all test results recorded on ${date} for this player? This cannot be undone.`)) return;
    try {
      await deleteTestSession(selectedPlayerId, date);
      await fetchPlayerData(selectedPlayerId);
    } catch {
      alert('Failed to delete test session.');
    }
  };

  const latestSprint   = useMemo(() => latestByPlayer(allData.sprint), [allData.sprint]);
  const latestCmj      = useMemo(() => latestByPlayer(allData.cmj), [allData.cmj]);
  const latestIsrt     = useMemo(() => latestByPlayer(allData.isrt), [allData.isrt]);
  const latestWeight   = useMemo(() => latestByPlayer(allData.weight), [allData.weight]);
  const latestSkinfold = useMemo(() => latestByPlayer(allData.skinfold), [allData.skinfold]);
  const latestHop      = useMemo(() => latestByPlayer(allData.hop), [allData.hop]);
  const latestStrengthByPlayer = useMemo(() => latestByPlayer(allData.strength), [allData.strength]);
  const latestPassing  = useMemo(() => latestByPlayer(allData.passingDrill), [allData.passingDrill]);
  // "Previous" test per player (second-most-recent), used only for the trend badges.
  const prevSprint   = useMemo(() => secondByPlayer(allData.sprint), [allData.sprint]);
  const prevCmj      = useMemo(() => secondByPlayer(allData.cmj), [allData.cmj]);
  const prevIsrt     = useMemo(() => secondByPlayer(allData.isrt), [allData.isrt]);
  const prevWeight   = useMemo(() => secondByPlayer(allData.weight), [allData.weight]);
  const prevSkinfold = useMemo(() => secondByPlayer(allData.skinfold), [allData.skinfold]);
  const prevHop      = useMemo(() => secondByPlayer(allData.hop), [allData.hop]);
  const prevStrengthByPlayer = useMemo(() => secondByPlayer(allData.strength), [allData.strength]);
  const prevPassing  = useMemo(() => secondByPlayer(allData.passingDrill), [allData.passingDrill]);
  const isrtPercentileByPlayer = useMemo(() => {
    const map = {};
    isrtPercentiles.forEach(r => { map[r.player_id] = r.percentile; });
    return map;
  }, [isrtPercentiles]);
  const sprintPercentileByPlayer = useMemo(() => {
    const map = {};
    sprintPercentiles.forEach(r => { map[r.player_id] = { p10: r.percentile_10m, p30: r.percentile_30m }; });
    return map;
  }, [sprintPercentiles]);

  const teamRows = useMemo(() => {
    return (players || [])
      .filter(p => p.active)
      .filter(p => p.name.toLowerCase().includes(filter.toLowerCase()) || (p.position || '').toLowerCase().includes(filter.toLowerCase()))
      .map(p => {
        const strengthRow  = latestStrengthByPlayer[p.id];
        const sprintRow    = latestSprint[p.id];
        const cmjRow       = latestCmj[p.id];
        const hopRow       = latestHop[p.id];
        const isrtRow      = latestIsrt[p.id];
        const passingRow   = latestPassing[p.id];
        const weightRow    = latestWeight[p.id];
        const skinfoldRow  = latestSkinfold[p.id];
        const prevStrengthRow = prevStrengthByPlayer[p.id];
        const prevSprintRow   = prevSprint[p.id];
        const prevCmjRow      = prevCmj[p.id];
        const prevHopRow      = prevHop[p.id];
        const prevIsrtRow     = prevIsrt[p.id];
        const prevPassingRow  = prevPassing[p.id];
        const prevWeightRow   = prevWeight[p.id];
        const prevSkinfoldRow = prevSkinfold[p.id];
        return {
          player: p,
          position:     p.position || null,
          sprint5m:     sprintRow?.sprint_5m ?? null,
          sprint10m:    sprintRow?.sprint_10m ?? null,
          sprint10mPercentile: sprintPercentileByPlayer[p.id]?.p10 ?? null,
          sprint30m:    sprintRow?.sprint_30m ?? null,
          sprint30mPercentile: sprintPercentileByPlayer[p.id]?.p30 ?? null,
          cmjHeight:    cmjRow?.height_cm ?? null,
          cmjPeakPower: cmjRow?.peak_power_wkg ?? null,
          cmjPeakPowerW: cmjRow ? cmjPeakPowerWatts(cmjRow.peak_power_wkg, p.weight) : null,
          hopRsi:       hopRow?.mean_rsi ?? null,
          isrtStage:    isrtRow?.final_stage ?? null,
          isrtPercentile: isrtPercentileByPlayer[p.id] ?? null,
          maxHR:        isrtRow?.hr_at_end ?? null,
          adjPctHRmax:  passingRow?.adjusted_pct_hrmax ?? null,
          weight:       weightRow?.weight_kg ?? null,
          sum8:         skinfoldRow?.sum_8 ?? null,
          hamstring:    strengthRow ? avg2(strengthRow.hamstring_left, strengthRow.hamstring_right) : null,
          quadriceps:   strengthRow ? avg2(strengthRow.quadriceps_left, strengthRow.quadriceps_right) : null,
          hq:           strengthRow ? hqRatio(strengthRow) : null,
          abad:         strengthRow ? abAdRatio(strengthRow) : null,
          dates: {
            sprint5m: sprintRow?.test_date, sprint10m: sprintRow?.test_date, sprint30m: sprintRow?.test_date,
            sprint10mPercentile: sprintRow?.test_date, sprint30mPercentile: sprintRow?.test_date,
            cmjHeight: cmjRow?.test_date, cmjPeakPower: cmjRow?.test_date, cmjPeakPowerW: cmjRow?.test_date,
            hopRsi: hopRow?.test_date,
            isrtStage: isrtRow?.test_date, isrtPercentile: isrtRow?.test_date, maxHR: isrtRow?.test_date,
            adjPctHRmax: passingRow?.test_date,
            weight: weightRow?.test_date,
            sum8: skinfoldRow?.test_date,
            hamstring: strengthRow?.test_date, quadriceps: strengthRow?.test_date,
            hq: strengthRow?.test_date, abad: strengthRow?.test_date,
          },
          trends: {
            sprint5m:     pctChange(sprintRow?.sprint_5m, prevSprintRow?.sprint_5m),
            sprint10m:    pctChange(sprintRow?.sprint_10m, prevSprintRow?.sprint_10m),
            sprint30m:    pctChange(sprintRow?.sprint_30m, prevSprintRow?.sprint_30m),
            cmjHeight:    pctChange(cmjRow?.height_cm, prevCmjRow?.height_cm),
            cmjPeakPower: pctChange(cmjRow?.peak_power_wkg, prevCmjRow?.peak_power_wkg),
            cmjPeakPowerW: pctChange(
              cmjRow ? cmjPeakPowerWatts(cmjRow.peak_power_wkg, p.weight) : null,
              prevCmjRow ? cmjPeakPowerWatts(prevCmjRow.peak_power_wkg, p.weight) : null,
            ),
            hopRsi:       pctChange(hopRow?.mean_rsi, prevHopRow?.mean_rsi),
            isrtStage:    pctChange(isrtRow?.final_stage, prevIsrtRow?.final_stage),
            maxHR:        pctChange(isrtRow?.hr_at_end, prevIsrtRow?.hr_at_end),
            adjPctHRmax:  pctChange(passingRow?.adjusted_pct_hrmax, prevPassingRow?.adjusted_pct_hrmax),
            weight:       pctChange(weightRow?.weight_kg, prevWeightRow?.weight_kg),
            sum8:         pctChange(skinfoldRow?.sum_8, prevSkinfoldRow?.sum_8),
            hamstring:    pctChange(
              strengthRow ? avg2(strengthRow.hamstring_left, strengthRow.hamstring_right) : null,
              prevStrengthRow ? avg2(prevStrengthRow.hamstring_left, prevStrengthRow.hamstring_right) : null,
            ),
            quadriceps: pctChange(
              strengthRow ? avg2(strengthRow.quadriceps_left, strengthRow.quadriceps_right) : null,
              prevStrengthRow ? avg2(prevStrengthRow.quadriceps_left, prevStrengthRow.quadriceps_right) : null,
            ),
            hq:   pctChange(strengthRow ? hqRatio(strengthRow) : null, prevStrengthRow ? hqRatio(prevStrengthRow) : null),
            abad: pctChange(strengthRow ? abAdRatio(strengthRow) : null, prevStrengthRow ? abAdRatio(prevStrengthRow) : null),
          },
        };
      })
      .sort((a, b) => {
        if (teamSortKey === 'name') {
          const cmp = a.player.name.localeCompare(b.player.name);
          return teamSortDir === 'asc' ? cmp : -cmp;
        }
        if (teamSortKey === 'position') {
          const cmp = (a.player.position || '').localeCompare(b.player.position || '');
          return teamSortDir === 'asc' ? cmp : -cmp;
        }
        // Missing results always sink to the bottom, regardless of sort direction.
        const av = a[teamSortKey], bv = b[teamSortKey];
        const aMissing = av === null || av === undefined || isNaN(av);
        const bMissing = bv === null || bv === undefined || isNaN(bv);
        if (aMissing || bMissing) return aMissing === bMissing ? 0 : (aMissing ? 1 : -1);
        const cmp = av - bv;
        return teamSortDir === 'asc' ? cmp : -cmp;
      });
  }, [players, filter, latestSprint, latestCmj, latestIsrt, latestWeight, latestSkinfold, latestHop, latestStrengthByPlayer, latestPassing, isrtPercentileByPlayer, sprintPercentileByPlayer, prevSprint, prevCmj, prevIsrt, prevWeight, prevSkinfold, prevHop, prevStrengthByPlayer, prevPassing, teamSortKey, teamSortDir]);

  const goToPlayer = id => { setSelectedPlayerId(String(id)); setView('individual'); };

  const toggleMetric = key =>
    setVisibleMetrics(prev => prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]);

  const allMetricsSelected = visibleMetrics.length === TEAM_METRICS.length;
  const toggleAllMetrics = () =>
    setVisibleMetrics(allMetricsSelected ? [] : TEAM_METRICS.map(m => m.key));

  const shownMetrics = TEAM_METRICS.filter(m => visibleMetrics.includes(m.key));

  const latestMobility = playerData.mobility?.[0] || {};
  const latestStrength = playerData.strength?.[0] || {};
  const latestDropjump = playerData.dropjump?.[0] || {};
  const latestDropjumpSL = playerData.dropjump_sl?.[0] || {};
  const hq = hqRatio(latestStrength);
  const abad = abAdRatio(latestStrength);

  // ── Trend chart data ──────────────────────────────────────────────────────
  const trendData = (rows, key) => [...(rows || [])].reverse().map(r => ({
    date: (r.test_date || '').slice(5, 10),
    value: r[key] != null ? Number(r[key]) : null,
  }));

  const CHARTS = [
    { title: 'Weight (kg)',            data: trendData(playerData.weight, 'weight_kg'),               color: '#3b82f6' },
    { title: 'CMJ Height (cm)',        data: trendData(playerData.cmj, 'height_cm'),                   color: '#10b981' },
    { title: 'CMJ Peak Power (W/kg)',  data: trendData(playerData.cmj, 'peak_power_wkg'),              color: '#ef4444' },
    { title: 'Adjusted Passing Drill %HRmax', data: trendData(playerData['passing-drill'], 'adjusted_pct_hrmax'), color: '#06b6d4', detail: true },
  ];

  // ── Test history (merged by date) ────────────────────────────────────────
  const historyRows = useMemo(() => {
    const byDate = {};
    const ensure = d => { if (!byDate[d]) byDate[d] = { date: d }; return byDate[d]; };
    // Sprint % only applies to each player's most recent test (the percentile comparison
    // set is frozen to "latest per player") so it's only populated on that one row, not
    // retroactively applied to older historical tests. Same convention as ISRT % below.
    const latestSprintDate = playerData.sprint?.[0]?.test_date?.slice(0, 10) || null;
    const mySprintPercentiles = sprintPercentiles.find(r => String(r.player_id) === String(selectedPlayerId));
    (playerData.sprint || []).forEach(r => {
      const date = r.test_date?.slice(0, 10);
      const row = ensure(date);
      row.sprint10m = r.sprint_10m;
      row.sprint30m = r.sprint_30m;
      row.sprint10mPercentile = date === latestSprintDate ? (mySprintPercentiles?.percentile_10m ?? null) : null;
      row.sprint30mPercentile = date === latestSprintDate ? (mySprintPercentiles?.percentile_30m ?? null) : null;
    });
    const selectedPlayerWeight = players.find(p => String(p.id) === String(selectedPlayerId))?.weight;
    (playerData.cmj || []).forEach(r => {
      const row = ensure(r.test_date?.slice(0, 10));
      row.cmjHeight = r.height_cm;
      row.cmjPeakPower = r.peak_power_wkg;
      row.cmjPeakPowerW = cmjPeakPowerWatts(r.peak_power_wkg, selectedPlayerWeight);
    });
    (playerData.hop || []).forEach(r => { const row = ensure(r.test_date?.slice(0, 10)); row.hopRsi = r.mean_rsi; });
    // ISRT % only applies to each player's most recent test (the percentile comparison
    // set is frozen to "latest per player" — see calculateISRTPercentile) so it's only
    // populated on that one row, not retroactively applied to older historical tests.
    const latestIsrtDate = playerData.isrt?.[0]?.test_date?.slice(0, 10) || null;
    const myIsrtPercentile = isrtPercentiles.find(r => String(r.player_id) === String(selectedPlayerId))?.percentile ?? null;
    (playerData.isrt || []).forEach(r => {
      const date = r.test_date?.slice(0, 10);
      const row = ensure(date);
      row.isrtStage = r.final_stage;
      row.isrtPercentile = date === latestIsrtDate ? myIsrtPercentile : null;
    });
    (playerData.weight || []).forEach(r => { const row = ensure(r.test_date?.slice(0, 10)); row.weight = r.weight_kg; });
    (playerData.skinfold || []).forEach(r => { const row = ensure(r.test_date?.slice(0, 10)); row.sum8 = r.sum_8; });
    (playerData['passing-drill'] || []).forEach(r => { const row = ensure(r.test_date?.slice(0, 10)); row.adjPctHRmax = r.adjusted_pct_hrmax; });
    (playerData.strength || []).forEach(r => { const row = ensure(r.test_date?.slice(0, 10)); row.hq = hqRatio(r); row.abad = abAdRatio(r); });
    (playerData.dropjump || []).forEach(r => { const row = ensure(r.test_date?.slice(0, 10)); row.dropjumpRsi = r.rsi; row.dropjumpPeakPower = r.peak_power_wkg; });
    return Object.values(byDate).sort((a, b) => {
      const cmp = sortKey === 'date' ? a.date.localeCompare(b.date) : (a[sortKey] ?? -Infinity) - (b[sortKey] ?? -Infinity);
      return sortDir === 'asc' ? cmp : -cmp;
    });
  }, [playerData, sortKey, sortDir, isrtPercentiles, sprintPercentiles, selectedPlayerId, players]);

  const toggleSort = key => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('desc'); }
  };

  const toggleTeamSort = key => {
    if (teamSortKey === key) setTeamSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setTeamSortKey(key); setTeamSortDir(key === 'name' || key === 'position' ? 'asc' : 'desc'); }
  };

  const handlePrint = () => {
    if (!selectedPlayerId) return;
    localStorage.setItem('print_physical_test_player', selectedPlayerId);
    window.open('/print/physical-testing', '_blank');
  };

  const inputStyle = { width: '100%', padding: '10px 14px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-primary)', fontSize: 14, outline: 'none' };
  const thStyle = { textAlign: 'left', padding: '8px 12px', fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, cursor: 'pointer', userSelect: 'none' };
  const tdStyle = { padding: '8px 12px', fontSize: 13 };

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <h1 style={{ fontSize: 22, fontWeight: 700 }}>Physical Testing</h1>
      </div>

      <div style={{ display: 'flex', gap: 4, background: 'var(--border)', borderRadius: 10, padding: 4, marginBottom: 20, maxWidth: 520, border: '1px solid var(--border-strong)' }}>
        {[{ id: 'team', label: 'Team Overview' }, { id: 'individual', label: 'Individual Player' }, { id: 'profiling', label: 'Squad Profiling' }].map(t => (
          <button key={t.id} onClick={() => setView(t.id)}
            style={{ flex: 1, padding: '10px', borderRadius: 8, border: 'none', cursor: 'pointer', background: view === t.id ? '#3b82f6' : 'transparent', color: view === t.id ? '#fff' : 'var(--text-secondary)', fontWeight: view === t.id ? 600 : 400, fontSize: 13 }}>
            {t.label}
          </button>
        ))}
      </div>

      {view === 'team' && (
        <div>
          <input placeholder="Filter by name or position..." value={filter} onChange={e => setFilter(e.target.value)} style={{ ...inputStyle, maxWidth: 320, marginBottom: 16 }} />

          <div style={{ marginBottom: 16 }}>
            <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, marginBottom: 8 }}>Show columns</div>
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              {TEAM_METRICS.map(m => {
                const active = visibleMetrics.includes(m.key);
                return (
                  <button key={m.key} onClick={() => toggleMetric(m.key)}
                    style={{ padding: '8px 14px', borderRadius: 10, border: `1px solid ${active ? '#3b82f6' : 'var(--border-strong)'}`, background: active ? '#3b82f6' : 'var(--bg-app)', color: active ? '#fff' : 'var(--text-secondary)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                    {m.label}
                  </button>
                );
              })}
            </div>
            <button onClick={toggleAllMetrics}
              style={{ marginTop: 8, padding: '8px 14px', borderRadius: 10, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-secondary)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              {allMetricsSelected ? 'Deselect all' : 'Select all'}
            </button>
          </div>

          <label style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 16, cursor: 'pointer', width: 'fit-content' }}>
            <input type="checkbox" checked={showTrend} onChange={e => setShowTrend(e.target.checked)} style={{ accentColor: '#3b82f6' }} />
            <span style={{ fontSize: 12, color: 'var(--text-secondary)' }}>Show trend vs previous test</span>
          </label>

          {loadingTeam ? (
            <div style={{ color: 'var(--text-muted)', padding: 40, textAlign: 'center' }}>Loading...</div>
          ) : (
            <div style={{ background: 'var(--border)', borderRadius: 12, border: '1px solid var(--border-strong)', overflow: 'hidden' }}>
              <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <thead>
                  <tr style={{ borderBottom: '1px solid var(--border-strong)' }}>
                    <th style={thStyle} onClick={() => toggleTeamSort('name')}>
                      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>Player <ArrowUpDown size={10} /></span>
                    </th>
                    {shownMetrics.map(m => (
                      <th key={m.key} style={thStyle} onClick={() => toggleTeamSort(m.key)}>
                        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>{m.label} <ArrowUpDown size={10} /></span>
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {teamRows.map(row => (
                    <tr key={row.player.id} onClick={() => goToPlayer(row.player.id)}
                      style={{ cursor: 'pointer', borderBottom: '1px solid var(--border-strong)' }}
                      onMouseEnter={e => e.currentTarget.style.background = 'var(--bg-app)'}
                      onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                      <td style={{ ...tdStyle, fontWeight: 600 }}>{row.player.name}</td>
                      {shownMetrics.map(m => {
                        const val = row[m.key];
                        const text = m.type === 'text'
                          ? (val || '—')
                          : (val === null || val === undefined || isNaN(val) ? '—' : `${Number(val).toFixed(m.digits)}${m.suffix || ''}`);
                        const color = m.color ? m.color(val ?? null) : undefined;
                        const date = row.dates?.[m.key];
                        const title = text !== '—' && date ? `Tested: ${date.slice(0, 10)}` : undefined;
                        const trendPct = showTrend && m.trend ? row.trends?.[m.key] : null;
                        return (
                          <td key={m.key} title={title}
                            style={{ ...tdStyle, ...(color ? { color, fontWeight: 700 } : {}), cursor: title ? 'help' : undefined }}>
                            <div>{text}</div>
                            {trendPct != null && (
                              <div style={{ fontSize: 9, fontWeight: 700, color: trendColor(trendPct, m.trend) }}>
                                {trendPct > 0 ? '+' : ''}{trendPct}%
                              </div>
                            )}
                          </td>
                        );
                      })}
                    </tr>
                  ))}
                  {teamRows.length === 0 && (
                    <tr><td colSpan={1 + shownMetrics.length} style={{ ...tdStyle, textAlign: 'center', color: 'var(--text-dimmed)', padding: 32 }}>No players found.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          )}
          <p style={{ fontSize: 11, color: 'var(--text-dimmed)', marginTop: 10 }}>
            ISRT % shows how a player's most recent ISRT MAX score compares to the rest of the squad. 100% means the best score in the team; 50% means half the squad scored higher. Calculated from each player's most recent test.
            <br />
            Sprint 10m % / Sprint 30m % work the same way but for time — 100% means the fastest time in the team; 50% means half the squad ran faster. Calculated from each player's most recent test.
          </p>
        </div>
      )}

      {view === 'individual' && (
        <div>
          <div style={{ display: 'flex', gap: 16, alignItems: 'flex-end', marginBottom: 20 }}>
            <div style={{ flex: 1, maxWidth: 320 }}>
              <label style={{ fontSize: 12, color: 'var(--text-secondary)', marginBottom: 6, display: 'block' }}>Player</label>
              <select value={selectedPlayerId} onChange={e => setSelectedPlayerId(e.target.value)} style={inputStyle}>
                <option value="">Select player...</option>
                {players.filter(p => p.active).sort((a, b) => a.name.localeCompare(b.name)).map(p => (
                  <option key={p.id} value={p.id}>{p.name}</option>
                ))}
              </select>
            </div>
            {selectedPlayerId && (
              <button onClick={handlePrint} style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 16px', borderRadius: 8, border: '1px solid var(--border-strong)', background: 'var(--bg-card)', color: 'var(--text-primary)', fontSize: 13, fontWeight: 600, cursor: 'pointer' }}>
                <Printer size={14} /> Export PDF
              </button>
            )}
          </div>

          {!selectedPlayerId ? (
            <div style={{ color: 'var(--text-dimmed)', padding: 40, textAlign: 'center' }}>Select a player to view their physical test data.</div>
          ) : loadingPlayer ? (
            <div style={{ color: 'var(--text-muted)', padding: 40, textAlign: 'center' }}>Loading...</div>
          ) : (
            <>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 20 }}>
                <div style={{ background: 'var(--border)', borderRadius: 12, padding: 20, border: '1px solid var(--border-strong)' }}>
                  <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>Mobility (latest: {latestMobility.test_date?.slice(0, 10) || '—'})</h3>
                  {MOBILITY_TESTS.map(t => (
                    <LRRowDisplay key={t.key} label={t.label} left={latestMobility[`${t.key}_left`]} right={latestMobility[`${t.key}_right`]} />
                  ))}
                </div>
                <div style={{ background: 'var(--border)', borderRadius: 12, padding: 20, border: '1px solid var(--border-strong)' }}>
                  <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>Strength (latest: {latestStrength.test_date?.slice(0, 10) || '—'})</h3>
                  {STRENGTH_TESTS.map(t => (
                    <LRRowDisplay key={t.key} label={t.label} left={latestStrength[`${t.key}_left`]} right={latestStrength[`${t.key}_right`]} />
                  ))}
                  <div style={{ display: 'flex', gap: 16, marginTop: 14 }}>
                    <div style={{ flex: 1, background: 'var(--bg-app)', borderRadius: 8, padding: 12, textAlign: 'center' }}>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Ratio H:Q</div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: hqColor(hq) }}>{hq !== null ? hq.toFixed(2) : '—'}</div>
                    </div>
                    <div style={{ flex: 1, background: 'var(--bg-app)', borderRadius: 8, padding: 12, textAlign: 'center' }}>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Ratio AB:AD</div>
                      <div style={{ fontSize: 18, fontWeight: 700, color: abAdColor(abad) }}>{abad !== null ? `${abad.toFixed(0)}%` : '—'}</div>
                    </div>
                  </div>
                </div>
                <div style={{ background: 'var(--border)', borderRadius: 12, padding: 20, border: '1px solid var(--border-strong)', gridColumn: '1 / -1' }}>
                  <h3 style={{ fontSize: 15, fontWeight: 700, marginBottom: 12 }}>Drop Jump</h3>
                  <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16 }}>
                    <div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>Bilateral (latest: {latestDropjump.test_date?.slice(0, 10) || '—'})</div>
                      <div style={{ display: 'flex', gap: 16 }}>
                        <div style={{ flex: 1, background: 'var(--bg-app)', borderRadius: 8, padding: 12, textAlign: 'center' }}>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>RSI</div>
                          <div style={{ fontSize: 18, fontWeight: 700 }}>{fmt(latestDropjump.rsi, 2)}</div>
                        </div>
                        <div style={{ flex: 1, background: 'var(--bg-app)', borderRadius: 8, padding: 12, textAlign: 'center' }}>
                          <div style={{ fontSize: 11, color: 'var(--text-muted)' }}>Peak Power (W/kg)</div>
                          <div style={{ fontSize: 18, fontWeight: 700 }}>{fmt(latestDropjump.peak_power_wkg, 1)}</div>
                        </div>
                      </div>
                    </div>
                    <div>
                      <div style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 8 }}>Single Leg (latest: {latestDropjumpSL.test_date?.slice(0, 10) || '—'})</div>
                      <LRRowDisplay label="RSI" left={latestDropjumpSL.rsi_left} right={latestDropjumpSL.rsi_right} />
                      <LRRowDisplay label="Peak Power (W/kg)" left={latestDropjumpSL.peak_power_wkg_left} right={latestDropjumpSL.peak_power_wkg_right} />
                    </div>
                  </div>
                </div>
              </div>

              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 16, marginBottom: 20 }}>
                {CHARTS.map(chart => (
                  <div key={chart.title} style={{ background: 'var(--border)', borderRadius: 12, padding: 16, border: '1px solid var(--border-strong)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                      <h4 style={{ fontSize: 12, fontWeight: 700, margin: 0 }}>{chart.title}</h4>
                      {chart.detail && (
                        <button onClick={() => setShowPassingDrillDetail(true)}
                          style={{ padding: '3px 10px', borderRadius: 6, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-secondary)', fontSize: 11, fontWeight: 600, cursor: 'pointer' }}>
                          Details
                        </button>
                      )}
                    </div>
                    {chart.data.length > 0 ? (
                      <ResponsiveContainer width="100%" height={140}>
                        <LineChart data={chart.data}>
                          <XAxis dataKey="date" stroke="var(--text-dimmed)" tick={{ fill: 'var(--text-secondary)', fontSize: 10 }} />
                          <YAxis stroke="var(--text-dimmed)" tick={{ fill: 'var(--text-secondary)', fontSize: 10 }} domain={['auto', 'auto']} />
                          <Tooltip contentStyle={{ background: 'var(--border)', border: '1px solid var(--border-strong)', borderRadius: 8, fontSize: 12 }} formatter={chart.detail ? (v => Number(v).toFixed(1)) : undefined} />
                          <Line type="monotone" dataKey="value" stroke={chart.color} strokeWidth={2} dot={{ r: 3 }} connectNulls />
                        </LineChart>
                      </ResponsiveContainer>
                    ) : (
                      <div style={{ height: 140, display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-dimmed)', fontSize: 12 }}>No data yet</div>
                    )}
                  </div>
                ))}
              </div>

              <div style={{ background: 'var(--border)', borderRadius: 12, border: '1px solid var(--border-strong)', overflow: 'hidden' }}>
                <div style={{ padding: '14px 20px', fontWeight: 700, fontSize: 15 }}>Test History</div>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid var(--border-strong)' }}>
                      {[
                        ['date', 'Date'], ['sprint10m', 'Sprint 10m'], ['sprint10mPercentile', 'Sprint 10m %'],
                        ['sprint30m', 'Sprint 30m'], ['sprint30mPercentile', 'Sprint 30m %'], ['cmjHeight', 'CMJ Height'],
                        ['cmjPeakPower', 'CMJ Peak Power (W/kg)'], ['cmjPeakPowerW', 'CMJ Peak Power (W)'], ['hopRsi', 'Hop RSI'],
                        ['dropjumpRsi', 'Drop Jump RSI'], ['dropjumpPeakPower', 'Drop Jump Peak Power (W/kg)'],
                        ['isrtStage', 'ISRT Stage'], ['isrtPercentile', 'ISRT %'], ['weight', 'Weight'], ['sum8', 'Sum-8'],
                        ['adjPctHRmax', 'Adj. %HRmax'], ['hq', 'H:Q'], ['abad', 'AB:AD'],
                      ].map(([key, label]) => (
                        <th key={key} style={thStyle} onClick={() => toggleSort(key)}>
                          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>{label} <ArrowUpDown size={10} /></span>
                        </th>
                      ))}
                      <th style={thStyle}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {historyRows.map(row => (
                      <tr key={row.date} style={{ borderBottom: '1px solid var(--border-strong)' }}>
                        <td style={{ ...tdStyle, fontWeight: 600 }}>{row.date}</td>
                        <td style={tdStyle}>{fmt(row.sprint10m, 2)}</td>
                        <td style={{ ...tdStyle, color: getPercentileColor(row.sprint10mPercentile ?? null), fontWeight: row.sprint10mPercentile != null ? 700 : 400 }}>
                          {row.sprint10mPercentile != null ? `${row.sprint10mPercentile}%` : '—'}
                        </td>
                        <td style={tdStyle}>{fmt(row.sprint30m, 2)}</td>
                        <td style={{ ...tdStyle, color: getPercentileColor(row.sprint30mPercentile ?? null), fontWeight: row.sprint30mPercentile != null ? 700 : 400 }}>
                          {row.sprint30mPercentile != null ? `${row.sprint30mPercentile}%` : '—'}
                        </td>
                        <td style={tdStyle}>{fmt(row.cmjHeight)}</td>
                        <td style={tdStyle}>{fmt(row.cmjPeakPower, 1)}</td>
                        <td style={tdStyle}>{fmt(row.cmjPeakPowerW, 0)}</td>
                        <td style={tdStyle}>{fmt(row.hopRsi, 2)}</td>
                        <td style={tdStyle}>{fmt(row.dropjumpRsi, 2)}</td>
                        <td style={tdStyle}>{fmt(row.dropjumpPeakPower, 1)}</td>
                        <td style={tdStyle}>{fmt(row.isrtStage, 0)}</td>
                        <td style={{ ...tdStyle, color: getISRTColor(row.isrtPercentile ?? null), fontWeight: row.isrtPercentile != null ? 700 : 400 }}>
                          {row.isrtPercentile != null ? `${row.isrtPercentile}%` : '—'}
                        </td>
                        <td style={tdStyle}>{fmt(row.weight)}</td>
                        <td style={tdStyle}>{fmt(row.sum8)}</td>
                        <td style={tdStyle}>{fmt(row.adjPctHRmax)}</td>
                        <td style={{ ...tdStyle, color: hqColor(row.hq ?? null) }}>{row.hq != null ? row.hq.toFixed(2) : '—'}</td>
                        <td style={{ ...tdStyle, color: abAdColor(row.abad ?? null) }}>{row.abad != null ? `${row.abad.toFixed(0)}%` : '—'}</td>
                        <td style={tdStyle}>
                          <div style={{ display: 'flex', gap: 8 }}>
                            <button onClick={() => handleEditSession(row.date)} title="Edit this session"
                              style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 6, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: 'var(--text-secondary)', cursor: 'pointer' }}>
                              <Pencil size={13} />
                            </button>
                            <button onClick={() => handleDeleteSession(row.date)} title="Delete this session"
                              style={{ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 28, height: 28, borderRadius: 6, border: '1px solid var(--border-strong)', background: 'var(--bg-app)', color: '#ef4444', cursor: 'pointer' }}>
                              <Trash2 size={13} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                    {historyRows.length === 0 && (
                      <tr><td colSpan={19} style={{ ...tdStyle, textAlign: 'center', color: 'var(--text-dimmed)', padding: 32 }}>No test history yet.</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <p style={{ fontSize: 11, color: 'var(--text-dimmed)', marginTop: 10 }}>
                ISRT % shows how a player's most recent ISRT MAX score compares to the rest of the squad. 100% means the best score in the team; 50% means half the squad scored higher. Calculated from each player's most recent test.
              </p>

              <div style={{ background: 'var(--border)', borderRadius: 12, border: '1px solid var(--border-strong)', overflow: 'hidden', marginTop: 16 }}>
                <div style={{ padding: '14px 20px', fontWeight: 700, fontSize: 15 }}>Notes</div>
                <div style={{ padding: '0 20px 16px' }}>
                  {playerNotes.length === 0 ? (
                    <div style={{ color: 'var(--text-dimmed)', fontSize: 13 }}>No notes recorded for this player.</div>
                  ) : playerNotes.map(n => (
                    <div key={n.id} style={{ padding: '10px 0', borderTop: '1px solid var(--border-strong)' }}>
                      <div style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 700, marginBottom: 4 }}>{(n.test_date || '').slice(0, 10)}</div>
                      <div style={{ fontSize: 13, color: 'var(--text-primary)', whiteSpace: 'pre-wrap' }}>{n.notes}</div>
                    </div>
                  ))}
                </div>
              </div>
            </>
          )}
        </div>
      )}

      {view === 'profiling' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
          <div style={{ background: 'var(--border)', borderRadius: 12, border: '1px solid var(--border-strong)', padding: 20 }}>
            <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Squad Profiling — Power</h2>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>
              Hop Test RSI vs CMJ Peak Power, plotted against the squad mean.
            </p>
            <QuadrantMatrix mode="squad" data={squadProfiling} />
          </div>

          <div style={{ background: 'var(--border)', borderRadius: 12, border: '1px solid var(--border-strong)', padding: 20 }}>
            <h2 style={{ fontSize: 16, fontWeight: 700, marginBottom: 4 }}>Squad Profiling — Acceleration & Endurance Capacity</h2>
            <p style={{ fontSize: 12, color: 'var(--text-muted)', marginBottom: 16 }}>
              Acceleration vs Endurance Capacity, plotted against the squad mean.
            </p>
            <QuadrantMatrix mode="squad" data={squadProfiling} metric={SPRINT_ISRT_METRIC} />
          </div>
        </div>
      )}

      {showPassingDrillDetail && (
        <PassingDrillDetailModal rows={allData.passingDrill} onClose={() => setShowPassingDrillDetail(false)} />
      )}
    </div>
  );
}
